/**
 * Browser socket for the SecurOne view (namespace /securone_live on the
 * alarm/socket server). One connection per page. The server decides which
 * rooms this account may follow (customer room, or one room per department it
 * belongs to) so a department user never receives a change event for a
 * department it cannot see (plan §8).
 *
 * Events are pings without content; the view refetches through the REST API,
 * which applies the access rules:
 *   securone.admin.changed    a category / group / user / document changed -> lists refetch
 *   securone.events.changed   an event was raised or changed -> the event list refetches
 *   securone.event.changed    the open event changed (status, acks, chat, log) -> detail refetches
 */
import { io } from "socket.io-client";
import store from "@/store";

let socket = null;
const listeners = {};
const joinedOrgs = new Set();
const joinedEvents = new Set();

const EVENTS = ["securone.admin.changed", "securone.events.changed", "securone.event.changed"];

function forward(name) {
  return (payload) => {
    (listeners[name] || []).forEach((fn) => {
      try {
        fn(payload);
      } catch (e) {
        console.error(e);
      }
    });
  };
}

export function connect() {
  if (socket) return socket;
  // Socket.IO reconnects by itself with backoff; no manual retry loop here.
  socket = io(`${process.env.VUE_APP_ALARM_SERVER}/securone_live`, {
    auth: { token: store.state.token },
    reconnectionDelay: 3000,
    reconnectionDelayMax: 30000,
    randomizationFactor: 0.5,
  });

  socket.on("connect", () => {
    joinedOrgs.forEach((id) => socket.emit("securone_join_org", id));
    joinedEvents.forEach((id) => socket.emit("securone_join_event", id));
  });

  // The token is refreshed by axios; hand the new one to the next attempt.
  socket.on("connect_error", () => {
    socket.auth.token = store.state.token;
  });

  EVENTS.forEach((name) => socket.on(name, forward(name)));
  socket.on("securone.admin.changed", () => store.commit("securone/BUMP_ADMIN"));

  return socket;
}

export function isConnected() {
  return !!(socket && socket.connected);
}

export function joinOrganisation(customerId) {
  if (!customerId) return;
  joinedOrgs.add(String(customerId));
  if (socket && socket.connected) socket.emit("securone_join_org", customerId);
}

export function joinEvent(eventId) {
  if (!eventId) return;
  joinedEvents.add(eventId);
  if (socket && socket.connected) socket.emit("securone_join_event", eventId);
}

export function leaveEvent(eventId) {
  joinedEvents.delete(eventId);
  if (socket && socket.connected) socket.emit("securone_leave_event", eventId);
}

export function on(name, fn) {
  listeners[name] = listeners[name] || [];
  listeners[name].push(fn);
  return () => off(name, fn);
}

export function off(name, fn) {
  listeners[name] = (listeners[name] || []).filter((f) => f !== fn);
}

export function disconnect() {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  joinedOrgs.clear();
  joinedEvents.clear();
  Object.keys(listeners).forEach((k) => delete listeners[k]);
}
